import React, { useEffect, useContext, useState } from 'react';
import { styled } from 'linaria/react';
import { useMutation } from '@apollo/react-hooks';
import { useQuery } from '@apollo/react-hooks';
import { useFormikContext } from 'formik';

import t from '@jetshop/intl';
import { CartIdContext } from '@jetshop/core/components/Cart/CartIdContext';
import {
  addToCartSuccess,
  useTrackProductAdd
} from '@jetshop/core/components/Mutation/AddToCart/addToCartUtils';
import { useNotification } from '@jetshop/core/components/Notifications';

import ProductToastWrapper from './ProductToast';
import { addToCart } from '../../../Cart/queries/addToCart.gql';
import { SVG } from '../../../../assets/SVG';
import { CTA } from 'r3-lib/dist/r3lib';
import customerApprovedQuery from './customerApprovedQuery.gql';
import { theme } from '../../../Theme';

const AddToCartQtyButtonContainer = styled('div')`
  display: flex;
  justify-content: space-between;
  width: 100%;
  grid-gap: 0px;

  .button-wrapper {
    display: flex;
    justify-content: space-between;
    flex: 1;

    .cta {
      width: 100%;
      height: 40px;
      border-radius: ${theme.button.desktop.borderRadius};
      svg {
        margin-right: 10px;
        margin-top: -3px;
        width: 15px;
      }

      .no-link {
        color: #fff;
        font-size: 11px;
        font-weight: 700;
        line-height: normal;
        letter-spacing: 1.1px;
        text-transform: uppercase;
      }
    }
  }
`;

const QtyAdjusterContainer = styled('div')`
  display: flex;
  align-items: center;

  button {
    width: 30px;
    height: 40px;
    background: #e9e5df;
    font-size: 30px;
    font-weight: 300;
    display: flex;
    justify-content: center;
    align-items: center;
    color: #282828;
    font-size: 14px;
    font-weight: normal;
  }
  .minus {
    border-radius: ${theme.button.buyArea.minusBorderRadius};
  }

  input {
    border: none;
    width: 25px;
    height: 40px;
    background: #e9e5df;
    text-align: center;
    font-size: 12px;
    color: #282828;
    &:focus {
      outline: none;
      border: 0;
    }

    /* --> Remove standard buttons from input <-- */
    /* Chrome, Safari, Edge, Opera */
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }

    /* Firefox */
    &[type='number'] {
      -moz-appearance: textfield;
    }

    :focus {
      outline: none;
      border: 1px solid black;
    }
  }
`;

export const AddToCartQtyButton = ({
  product,
  variant,
  productPage,
  outOfStockNotify,
  validationMessage
}) => {
  const qty = 1;
  const { cartId, setCartId } = useContext(CartIdContext);
  const articleNumber = variant?.articleNumber ?? product.articleNumber;
  const price = {
    price: variant?.price ?? product.price,
    previousPrice: variant?.previousPrice ?? product.previousPrice
  };

  const trackAddProductVariant = useTrackProductAdd(variant ?? product);

  const [trigger] = useNotification();
  const { values } = useFormikContext();

  const commentCreator = vals => {
    const totalCommentsArray = [];
    const commentsArray = Object.entries(vals?.comments);
    commentsArray.forEach(comment => {
      const key = comment?.[0];
      const value = comment?.[1];
      let commentObj = {};
      commentObj['name'] = key;
      commentObj['value'] = value;

      totalCommentsArray.push({ ...commentObj });
    });

    return totalCommentsArray;
  };

  const [add] = useMutation(addToCart, {
    variables: {
      input: {
        cartId,
        articleNumber,
        quantity: qty,
        comments: commentCreator(values)
      }
    },
    onCompleted: data => {
      trigger(
        <ProductToastWrapper
          selectedVariation={variant}
          product={product}
          quantity={qty}
          price={price}
        />,
        { type: 'add-to-cart' }
      );

      trackAddProductVariant(product, qty);

      addToCartSuccess({ cartId, setCartId })({ data });
    }
  });

  const checkVariantsBuyable = product?.hasVariants
    ? variant?.stockStatus?.buyable
    : product?.stockStatus?.buyable;

  return (
    <>
      <AddToCartQtyButtonContainer className="add-to-cart-qty-button-container">
        {productPage ? (
          <div className="button-wrapper">
            {!outOfStockNotify && checkVariantsBuyable ? (
              <CTA
                testid="add-to-cart"
                className={'accent'}
                clickCallback={add}
              >
                <>
                  <SVG name="Cart" />
                  {validationMessage ? validationMessage : t('Add to cart')}
                </>
              </CTA>
            ) : (
              <CTA testid="add-to-cart" className={'accent disabled'}>
                <>
                  <SVG name="Cart" />
                  {validationMessage ? validationMessage : t('Add to cart')}
                </>
              </CTA>
            )}
          </div>
        ) : (
          <CTA className={'accent'} clickCallback={add}>
            <>
              <SVG name="Cart" />
              {validationMessage ? validationMessage : t('Add to cart')}
            </>
          </CTA>
        )}
      </AddToCartQtyButtonContainer>
    </>
  );
};
